/* RLA shipping estimator. Both sections load this classic deferred script BEFORE
 * cart.js. Its guarded definitions win the stock registration guards, including
 * forms inserted later by APIButton's replaceWith. No BaseElement dependency or
 * observer is needed. The stock form/is attributes and alert children remain a
 * working fallback if this asset fails to load. Non-estimator country-province
 * elements retain the address editor's country/province selection contract.
 *
 * ZIP3 ranges cover domestic states, DC, PR, VI, AA/AE/AP and Pacific territories.
 * ZIP5 exceptions resolve prefixes shared across state/territory boundaries.
 * This is routing, not an address-existence database. Shopify remains the rate
 * authority. Match the derived code to Shopify's actual data-provinces option;
 * never send a guessed or empty province for US/CA.
 */
(() => {
  'use strict';

  const ZIP3_RANGES = {
    NY: '005,100-149', PR: '006-007,009', VI: '008', MA: '010-027,055',
    RI: '028-029', NH: '030-038', ME: '039-049', VT: '050-054,056-059',
    CT: '060-069', NJ: '070-089', AE: '090-098', PA: '150-196',
    DE: '197-199', DC: '200,202-205,569', VA: '201,220-246', MD: '206-219',
    WV: '247-268', NC: '270-289', SC: '290-299', GA: '300-319,398-399',
    FL: '320-339,341-349', AA: '340', AL: '350-369', TN: '370-385',
    MS: '386-397', KY: '400-427', OH: '430-459', IN: '460-479', MI: '480-499',
    IA: '500-528', WI: '530-549', MN: '550-567', SD: '570-577', ND: '580-588',
    MT: '590-599', IL: '600-629', MO: '630-658', KS: '660-679', NE: '680-693',
    LA: '700-714', AR: '716-729', OK: '730-731,734-749', TX: '733,750-799,885',
    CO: '800-816', WY: '820-831', ID: '832-838', UT: '840-847',
    AZ: '850-853,855-857,859-860,863-865', NM: '870-884', NV: '889-898',
    CA: '900-908,910-928,930-961', AP: '962-966', HI: '967-968', GU: '969',
    OR: '970-979', WA: '980-986,988-994', AK: '995-999'
  };
  const ZIP3 = Object.create(null);
  for (const [province, ranges] of Object.entries(ZIP3_RANGES)) {
    for (const range of ranges.split(',')) {
      const [start, end = start] = range.split('-').map(Number);
      for (let n = start; n <= end; n++) ZIP3[String(n).padStart(3, '0')] = province;
    }
  }
  const ZIP5_EXCEPTIONS = {
    '06390': 'NY', '83414': 'WY', '96799': 'AS',
    '96940': 'PW', '96941': 'FM', '96942': 'FM', '96943': 'FM', '96944': 'FM',
    '96950': 'MP', '96951': 'MP', '96952': 'MP', '96960': 'MH', '96970': 'MH'
  };
  const CANADA = {
    A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC',
    K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK',
    T: 'AB', V: 'BC', Y: 'YT'
  };
  const PROVINCE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
    PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    PR: 'Puerto Rico', VI: 'Virgin Islands', AS: 'American Samoa', GU: 'Guam',
    MP: 'Northern Mariana Islands', FM: 'Federated States of Micronesia',
    MH: 'Marshall Islands', PW: 'Palau', AA: 'Armed Forces Americas',
    AE: 'Armed Forces Europe', AP: 'Armed Forces Pacific',
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
    NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
    SK: 'Saskatchewan', YT: 'Yukon'
  };
  function provinceForPostal(country, postal) {
    const code = String(postal).trim().toUpperCase();
    if (country === 'US') {
      if (!/^\d{5}(?:-\d{4})?$/.test(code)) return null;
      return ZIP5_EXCEPTIONS[code.slice(0, 5)] || ZIP3[code.slice(0, 3)] || null;
    }
    if (country === 'CA') {
      if (!/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ] ?\d[ABCEGHJ-NPRSTVWXYZ]\d$/.test(code)) return null;
      if (code[0] === 'X') return /^X0[ABC]/.test(code) ? 'NU' : 'NT';
      return CANADA[code[0]] || null;
    }
    return null;
  }
  function deliveryEstimate(rate) {
    const raw = rate.delivery_days ?? rate.delivery_range;
    let days = [];
    if (Array.isArray(raw)) days = raw;
    else if (raw && typeof raw === 'object') days = [raw.min ?? raw.minimum, raw.max ?? raw.maximum];
    else if (typeof raw === 'number') days = [raw];
    else if (typeof raw === 'string' && /^\s*\d+(?:\s*(?:-|\u2013|\u2014|to)\s*\d+)?\s*$/.test(raw)) days = raw.match(/\d+/g);
    days = days.filter((day) => day !== null && day !== undefined && day !== '').map(Number).filter((day) => Number.isFinite(day) && day >= 0);
    if (!days.length) return '';
    const min = Math.min(...days), max = Math.max(...days);
    return min === max ? `${min} business ${min === 1 ? 'day' : 'days'}` : `${min} to ${max} business days`;
  }
  // Local tests import the SAME routing functions used by the browser.
  // One readable line under a rate: its description, then the carrier's transit
  // estimate as a sentence (owner, 2026-11-02: "delivery in 4 business days", not a
  // bare "4 business days"). A description that already states delivery timing
  // (Overnight's "next business day delivery") is not repeated.
  function rateDetail(rate) {
    let text = String(rate.description || '').trim();
    if (text && !/[.!?]$/.test(text)) text += '.';
    const estimate = deliveryEstimate(rate);
    if (estimate && !/\bdelivery\b/i.test(text)) text = `${text} Delivery in ${estimate} once shipped.`.trim();
    return text;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { provinceForPostal, deliveryEstimate, rateDetail, ZIP3 };
  if (typeof window === 'undefined') return;

  const strings = () => ({
    error: 'We could not get shipping rates. Please try again.',
    notFound: 'No shipping rates were found for this address. Check your postal code or try again.',
    oneResult: 'Found 1 shipping rate.', multipleResults: 'Found [count] shipping rates.',
    invalidUS: 'Enter a valid 5 digit ZIP code or ZIP+4, such as 90210 or 90210-1234.',
    invalidCA: 'Enter a valid Canadian postal code, such as M5V 2T6.',
    invalidProvince: 'We could not match that postal code to a state or province for this country. Check the code and country.',
    selectProvince: 'Choose a state or province.', selectCountry: 'Choose a country.',
    ...(window.theme?.shippingCalculatorStrings || {})
  });
  const readable = (value) => String(value ?? '').replace(/[\u2013\u2014]/g, ' ');

  // Shopify may emit English country names as option values. Canonicalize those
  // values to ISO codes before matching Liquid's country_code/iso_code. Never
  // match translated display text. data-code and ISO option values also work.
  let countryCodes;
  function countryCode(option) {
    if (option.dataset.code) return option.dataset.code.toUpperCase();
    if (/^[A-Z]{2}$/.test(option.value)) return option.value;
    if (!countryCodes) {
      countryCodes = new Map(Object.entries({
        'United States': 'US', Canada: 'CA', 'United Kingdom': 'GB',
        'Czech Republic': 'CZ', Turkey: 'TR', 'Cape Verde': 'CV', Swaziland: 'SZ',
        'Macedonia, Republic Of': 'MK', 'Macedonia': 'MK', 'East Timor': 'TL',
        'Holy See (Vatican City State)': 'VA', 'Vatican City State (Holy See)': 'VA',
        'Russian Federation': 'RU', 'Iran, Islamic Republic Of': 'IR',
        'Moldova, Republic of': 'MD', "Lao People's Democratic Republic": 'LA',
        'Bolivia, Plurinational State Of': 'BO', 'Venezuela, Bolivarian Republic Of': 'VE',
        'Brunei Darussalam': 'BN', 'Syrian Arab Republic': 'SY',
        'United States Minor Outlying Islands': 'UM', 'Virgin Islands, U.S.': 'VI',
        'Virgin Islands, British': 'VG', 'Congo, The Democratic Republic Of The': 'CD',
        'Korea, Republic Of': 'KR', 'Korea, Democratic People\'s Republic Of': 'KP',
        'Taiwan, Province Of China': 'TW', 'Tanzania, United Republic Of': 'TZ'
      }));
      if (typeof Intl.DisplayNames === 'function') {
        const names = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });
        for (let a = 65; a <= 90; a++) for (let b = 65; b <= 90; b++) {
          const iso = String.fromCharCode(a, b), name = names.of(iso);
          if (name && !countryCodes.has(name)) countryCodes.set(name, iso);
        }
      }
    }
    return countryCodes.get(option.value) || option.value;
  }

  if (!customElements.get('country-province')) {
    customElements.define('country-province', class extends HTMLElement {
      connectedCallback() {
        this.initialize();
      }
      initialize() {
        if (this.connected) return;
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.provinceElement = this.querySelector('[name="address[province]"]');
        if (!this.countryElement || !this.provinceElement) return;
        this.connected = true;
        this.estimator = this.closest('[data-rla-ship-estimate]');
        if (this.estimator) {
          for (const option of this.countryElement.options) option.value = countryCode(option);
          const preferences = [this.dataset.customerCountry, this.dataset.defaultCountry, 'US'];
          for (const code of preferences) {
            if (code && Array.from(this.countryElement.options).some((option) => option.value === code)) {
              this.countryElement.value = code;
              break;
            }
          }
        } else if (this.dataset.country) {
          const match = Array.from(this.countryElement.options).find((option) => option.value === this.dataset.country || option.textContent === this.dataset.country);
          if (match) this.countryElement.value = match.value;
        }
        this.changeHandler = () => this.handleCountryChange();
        this.countryElement.addEventListener('change', this.changeHandler);
        this.handleCountryChange();
      }
      handleCountryChange() {
        const option = this.countryElement.selectedOptions[0];
        let provinces = [];
        try { provinces = JSON.parse(option?.dataset.provinces || '[]'); } catch { /* No province choices. */ }
        this.provinceElement.replaceChildren();
        for (const [value, label] of provinces) {
          const selected = value === this.dataset.province || label === this.dataset.province;
          this.provinceElement.add(new Option(label, value, selected, selected));
        }
        const automatic = this.estimator && ['US', 'CA'].includes(this.countryElement.value);
        this.provinceElement.parentElement.hidden = automatic || !provinces.length;
        this.provinceElement.required = !automatic && provinces.length > 0;
        if (!provinces.length || automatic) this.provinceElement.value = '';
        this.countryElement.setAttribute('selected', '');
        this.provinceElement.toggleAttribute('selected', this.provinceElement.value !== '');
        this.estimator?.syncPostal?.();
      }
      disconnectedCallback() {
        this.countryElement?.removeEventListener('change', this.changeHandler);
        this.connected = false;
      }
    });
  }

  if (!customElements.get('shipping-calculator')) {
    customElements.define('shipping-calculator', class extends HTMLFormElement {
      connectedCallback() {
        if (this.listeners) return;
        this.countryProvince = this.querySelector('country-province');
        this.countryProvince.initialize?.();
        this.country = this.querySelector('[name="address[country]"]');
        this.province = this.querySelector('[name="address[province]"]');
        this.zip = this.querySelector('[name="address[zip]"]');
        this.button = this.querySelector('[type="submit"]');
        this.results = this.lastElementChild;
        this.results.className = 'rla-ship-estimate__results';
        this.results.setAttribute('aria-live', 'polite');
        this.results.setAttribute('aria-atomic', 'true');
        this.results.replaceChildren();
        this.results.hidden = false;
        this.feedback = document.createElement('p');
        this.feedback.className = 'rla-ship-estimate__validation';
        this.feedback.id = `${this.zip.id}-error`;
        this.feedback.setAttribute('aria-live', 'polite');
        this.feedback.hidden = true;
        this.zip.parentElement.append(this.feedback);
        this.zip.setAttribute('aria-describedby', this.feedback.id);
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        // Capture owns busy state before HoverButton's bubbling submit listener.
        this.addEventListener('submit', (event) => this.onSubmit(event), { ...options, capture: true });
        this.addEventListener('input', () => this.onAddressChange(), options);
        this.addEventListener('change', () => this.onAddressChange(), options);
        this.zip.addEventListener('blur', () => this.validatePostal(false), options);
        this.syncPostal();
      }
      disconnectedCallback() {
        this.cancel();
        this.listeners?.abort();
        this.listeners = null;
        this.feedback?.remove();
      }
      cancel() {
        this.request?.abort();
        this.button?.removeAttribute('aria-busy');
      }
      onAddressChange() {
        this.cancel();
        this.results.replaceChildren();
        this.showPostalError('');
        this.syncPostal();
      }
      syncPostal() {
        if (!this.zip) return;
        const automatic = ['US', 'CA'].includes(this.country.value);
        this.zip.required = automatic;
        this.zip.inputMode = this.country.value === 'US' ? 'numeric' : 'text';
        if (automatic) {
          const code = provinceForPostal(this.country.value, this.zip.value);
          const match = Array.from(this.province.options).find((option) => option.value === code || option.value === PROVINCE_NAMES[code] || option.textContent === PROVINCE_NAMES[code]);
          this.province.value = match?.value || '';
          this.province.toggleAttribute('selected', Boolean(match));
        }
      }
      showPostalError(message) {
        this.feedback.textContent = message;
        this.feedback.hidden = !message;
        this.zip.toggleAttribute('aria-invalid', Boolean(message));
        if (message) this.zip.setAttribute('aria-invalid', 'true');
        this.zip.setCustomValidity(message);
      }
      validatePostal(focus) {
        this.syncPostal();
        if (!['US', 'CA'].includes(this.country.value)) return true;
        const code = provinceForPostal(this.country.value, this.zip.value);
        const message = !code ? strings()[this.country.value === 'US' ? 'invalidUS' : 'invalidCA'] : !this.province.value ? strings().invalidProvince : '';
        this.showPostalError(message);
        if (message && focus) this.zip.focus();
        return !message;
      }
      async fetchRates(path, address, signal, method = 'POST') {
        const root = (window.theme?.routes.cart_url || '/cart').replace(/\/$/, '');
        const query = new URLSearchParams();
        for (const [key, value] of Object.entries(address)) query.set(`shipping_address[${key}]`, value);
        const response = await fetch(`${root}/${path}.json${method === 'GET' ? `?${query}` : ''}`, {
          method, signal, credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          ...(method === 'POST' ? { body: JSON.stringify({ shipping_address: address }) } : {})
        });
        if (response.status === 202 || response.status === 204) return null;
        let payload;
        try { payload = await response.json(); } catch { throw new Error(strings().error); }
        if (!response.ok || (payload && payload.shipping_rates !== null && !Array.isArray(payload.shipping_rates) && Object.keys(payload).length)) {
          throw new Error(strings().error);
        }
        return payload?.shipping_rates || null;
      }
      async onSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.button.hasAttribute('aria-busy')) return;
        this.results.replaceChildren();
        if (!this.validatePostal(true)) return;
        if (!this.country.value) { this.showMessage(strings().selectCountry, true); this.country.focus(); return; }
        if (this.province.required && !this.province.value) { this.showMessage(strings().selectProvince, true); this.province.focus(); return; }
        const postal = this.zip.value.trim().toUpperCase();
        this.zip.value = this.country.value === 'CA' ? postal.replace(/\s/g, '').replace(/^(.{3})(.{3})$/, '$1 $2') : postal;
        const address = { zip: this.zip.value, country: this.country.value, province: this.province.value };
        this.cancel();
        const controller = new AbortController();
        this.request = controller;
        this.button.setAttribute('aria-busy', 'true');
        let timedOut = false;
        const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
        try {
          let rates = await this.fetchRates('shipping_rates', address, controller.signal);
          if (!rates?.length) {
            const prepared = await this.fetchRates('prepare_shipping_rates', address, controller.signal);
            if (prepared?.length) rates = prepared;
            for (let attempt = 0; !rates?.length && attempt < 5; attempt++) {
              await new Promise((resolve, reject) => {
                const abort = () => { clearTimeout(wait); reject(new DOMException('Aborted', 'AbortError')); };
                const wait = setTimeout(() => { controller.signal.removeEventListener('abort', abort); resolve(); }, 500 + attempt * 250);
                if (controller.signal.aborted) abort();
                else controller.signal.addEventListener('abort', abort, { once: true });
              });
              rates = await this.fetchRates('async_shipping_rates', address, controller.signal, 'GET');
            }
          }
          if (!controller.signal.aborted) this.renderRates(rates || []);
        } catch (error) {
          if (!controller.signal.aborted || timedOut) this.showMessage(strings().error, true);
        } finally {
          clearTimeout(timeout);
          if (this.request === controller) this.button.removeAttribute('aria-busy');
        }
      }
      showMessage(message, error = false) {
        const heading = document.createElement(error ? 'p' : 'h3');
        heading.className = error ? 'rla-ship-estimate__error' : 'rla-ship-estimate__summary';
        heading.textContent = readable(message);
        this.results.replaceChildren(heading);
      }
      formatPrice(rate) {
        const amount = Number(rate.price);
        const format = window.theme?.settings?.moneyWithCurrencyFormat || window.theme?.settings?.moneyFormat;
        const activeCurrency = window.Shopify?.currency?.active;
        if (format && window.theme?.Currency?.formatMoney && (!activeCurrency || !rate.currency || rate.currency === activeCurrency)) {
          const text = document.createElement('span');
          text.innerHTML = window.theme.Currency.formatMoney(Math.round(amount * 100), format);
          return text.textContent;
        }
        try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: rate.currency || activeCurrency || 'USD' }).format(amount); }
        catch { return `${rate.currency || activeCurrency || 'USD'} ${amount.toFixed(2)}`; }
      }
      renderRates(rates) {
        if (!rates.length) { this.showMessage(strings().notFound); return; }
        this.showMessage((rates.length === 1 ? strings().oneResult : strings().multipleResults).replaceAll('[count]', String(rates.length)));
        const list = document.createElement('ul');
        list.className = 'rla-ship-estimate__rates';
        list.setAttribute('role', 'list');
        for (const rate of [...rates].sort((a, b) => Number(a.price) - Number(b.price))) {
          const row = document.createElement('li');
          row.className = 'rla-ship-estimate__rate';
          const name = document.createElement('span');
          name.className = 'rla-ship-estimate__name';
          name.textContent = readable(rate.presentment_name || rate.name || 'Shipping');
          const price = document.createElement('span');
          price.className = 'rla-ship-estimate__price';
          price.textContent = this.formatPrice(rate);
          row.append(name, price);
          const detailText = rateDetail(rate);
          if (detailText) {
            const detail = document.createElement('p');
            detail.className = 'rla-ship-estimate__detail';
            detail.textContent = readable(detailText);
            row.append(detail);
          }
          list.append(row);
        }
        this.results.append(list);
      }
    }, { extends: 'form' });
  }
})();
